import type {
  DocumentChangeAuditDTO,
  DocumentChangeAuditPaginationDTO,
  DocumentChangeReportByEntityTypeDTO,
  DocumentChangeReportSummaryDTO,
  DocumentChangeReportTotalsDTO,
} from "../models/DocumentChangeAuditDTOs";
import type {
  DocumentChangeAuditEntity,
  DocumentChangeAuditPaginationEntity,
  DocumentChangeAuditsResultEntity,
  DocumentChangeReportByEntityTypeEntity,
  DocumentChangeReportSummaryEntity,
  DocumentChangeReportTotalsEntity,
} from "../../domain/entities/DocumentChangeAuditEntity";

export class DocumentChangeAuditMapper {
  static toAuditEntity(dto: DocumentChangeAuditDTO): DocumentChangeAuditEntity {
    return {
      id: dto.id,
      userId: dto.user_id,
      userEmail: dto.user_email ?? null,
      userName: dto.user_name ?? null,
      userRole: dto.user_role ?? null,
      entityType: dto.entity_type,
      entityId: dto.entity_id,
      moduleCode: dto.module_code,
      actionCode: dto.action_code,
      zohoOperation: dto.zoho_operation,
      reason: dto.reason,
      changeDetails: dto.change_details ?? {},
      createdAt: dto.created_at,
      documentNumber: dto.document_number ?? null,
      partyName: dto.party_name ?? null,
      uploadedAt: dto.uploaded_at ?? null,
      postedToZohoDate: dto.posted_to_zoho_date ?? null,
      documentTotal: dto.document_total ?? null,
      hasMeaningfulChanges: Boolean(dto.has_meaningful_changes),
      changeCount: dto.change_count ?? 0,
    };
  }

  static toAuditEntityArray(
    dtos: DocumentChangeAuditDTO[],
  ): DocumentChangeAuditEntity[] {
    return dtos.map((dto) => this.toAuditEntity(dto));
  }

  static toTotalsEntity(
    dto: DocumentChangeReportTotalsDTO,
  ): DocumentChangeReportTotalsEntity {
    return {
      allPosted: dto.all_posted,
      postedBills: dto.posted_bills,
      postedInvoices: dto.posted_invoices,
      withChanges: dto.with_changes,
      withoutChanges: dto.without_changes,
      editRatePercent: dto.edit_rate_percent,
    };
  }

  static toByEntityTypeEntity(
    dto: DocumentChangeReportByEntityTypeDTO,
  ): DocumentChangeReportByEntityTypeEntity {
    return {
      posted: dto.posted,
      withChanges: dto.with_changes,
      withoutChanges: dto.without_changes,
    };
  }

  static toSummaryEntity(
    dto: DocumentChangeReportSummaryDTO,
  ): DocumentChangeReportSummaryEntity {
    return {
      totals: this.toTotalsEntity(dto.totals),
      byEntityType: {
        bill: this.toByEntityTypeEntity(dto.by_entity_type.bill),
        invoice: this.toByEntityTypeEntity(dto.by_entity_type.invoice),
      },
      fieldFrequency: {
        bill: dto.field_frequency?.bill ?? [],
        invoice: dto.field_frequency?.invoice ?? [],
      },
    };
  }

  static toPaginationEntity(
    dto: DocumentChangeAuditPaginationDTO,
  ): DocumentChangeAuditPaginationEntity {
    return {
      total: dto.total,
      limit: dto.limit,
      offset: dto.offset,
      hasMore: dto.has_more,
    };
  }

  static toAuditsResultEntity(
    audits: DocumentChangeAuditDTO[],
    summary: DocumentChangeReportSummaryDTO,
    pagination: DocumentChangeAuditPaginationDTO,
  ): DocumentChangeAuditsResultEntity {
    return {
      audits: this.toAuditEntityArray(audits),
      summary: this.toSummaryEntity(summary),
      pagination: this.toPaginationEntity(pagination),
    };
  }
}
